/**
 * icons.js — cumulus / rain / lightning icon appenders.
 *
 * All three functions append SVG elements into a d3 selection.  They are
 * validated visually (Task 13) rather than unit-tested here.
 *
 * Cumulus glyph reuses the CUMULUS_PATH from CloudIcon.js (Skysight's own
 * emoji-style cloud icon, 72×72 viewBox).  Rain / lightning use simple
 * geometric shapes (rendered faithfully to the bundle's HTML-injected SVG
 * strings, which reference an external bundle not available to us).
 *
 * All signatures:
 *
 *   drawCumulus(sel, columns, data, yScale, width, gt, margin)
 *   drawRain(sel, columns, data, yScale, width, gt, margin, chartHeight)
 *   drawLightning(sel, columns, data, yScale, width, gt, margin)
 *
 * Parameters:
 *   sel          — d3 selection of the parent <svg> element
 *   columns      — output of cumulusColumns(data): [{index, cu_oktas, accrain, cape}]
 *   data         — raw parsed windgram data array (same .data from API response),
 *                  each element has { pblh, zsfclcl, ... }
 *   yScale       — d3 scale: altitude (metres) → pixel y
 *   width        — inner chart pixel width
 *   gt           — horizontal scale factor (0.33 / 0.68 / 1)
 *   margin       — { left, top } chart margins (use MARGIN from constants.js)
 *   chartHeight  — inner chart pixel height (used by drawRain for column extent)
 *
 * Implementation notes (ported from bundle lines 1440-1565):
 *  - Bundle places icons by walking along the condensation-layer path
 *    (SVGPathElement.getPointAtLength).  We simplify: compute x from the
 *    column index and y from yScale(zsfclcl) + yScale(pblh) midpoint.
 *    Exact placement is validated / tuned in Task 13.
 *  - cu_oktas determines the number of columns per timestep (1–3) and base
 *    icon size.  accrain overrides the cloud type with a rain-topped variant.
 *    cape > 1000 + cu_oktas > 2 + pblh-zsfclcl > 300 triggers lightning.
 */

// ─── Cumulus path (from CloudIcon.js) ───────────────────────────────────────
const CUMULUS_PATH =
  'M15.9345,30.2552c-0.0372,0.4242-0.3809,0.7675-0.8065,0.8131C9.9685,31.621,6,36.7013,6,42.8852 c0,6.553,4.5445,11.8652,10.1505,11.8652h38.6977C61.0072,54.7504,66,49.1365,66,42.2114c0-6.6366-4.5855-12.069-10.3883-12.5101 c-0.4439-0.0337-0.804-0.3445-0.8887-0.7793c-1.2963-6.6567-7.2763-11.659-14.3464-11.659c-4.5948,0-8.6879,2.0802-11.3459,5.3894 c-0.2597,0.3234-0.6956,0.4715-1.079,0.3107c-1.0228-0.4289-2.103-0.6365-3.3117-0.6365 C20.0727,22.3266,16.3237,25.8122,15.9345,30.2552z';

// Rain drop path (simple teardrop, 72×72 viewBox)
const RAIN_PATH =
  'M36,10 C36,10 14,38 14,50 C14,61.046 24.059,70 36,70 C47.941,70 58,61.046 58,50 C58,38 36,10 36,10 Z';

// Lightning bolt path (72×72 viewBox)
const LIGHTNING_PATH =
  'M42,5 L20,42 L32,42 L28,68 L52,28 L40,28 Z';

// Cumulus FILL by cu_oktas — VERIFIED against Skysight's bundle. Skysight picks
// one of 5 emoji cumulus glyphs by oktas (bundle: W<=2?Ua:W<=4?Ea:W<=6?Wa:W<=7?Ye:Ha).
// Extracted glyph fills: Ua AND Ea are BOTH #FFFFFF (white), Wa=#E6E6E6,
// Ye=#B3B3B3, Ha=#808080. So clouds stay WHITE up to 4 oktas and only gray out
// for well-developed cu — which is why Skysight reads as mostly white with gray
// accents. We reuse the one CUMULUS_PATH and swap the fill to match.
function cloudFillForOktas(oktas) {
  if (oktas <= 4) return '#FFFFFF'; // Ua (≤2) and Ea (≤4) both white
  if (oktas <= 6) return '#E6E6E6'; // Wa
  if (oktas <= 7) return '#B3B3B3'; // Ye
  return '#808080'; // Ha (>7)
}

// ─── Size helpers ─────────────────────────────────────────────────────────────

/**
 * Base icon size from cu_oktas (bundle: ht = 30; scaled by Rt count).
 * Rt (columns per timestep): oktas > 6 → 3, > 3 → 2, else 1.
 */
function iconSize(cu_oktas, rt) {
  let ht = 30;
  if (rt === 2) ht *= 0.85;
  else if (rt === 1) ht *= 0.7225;
  return ht;
}

/**
 * Number of icon columns per timestep (bundle line 1481).
 * Rt: oktas > 6 → 3, > 3 → 2, else 1.
 */
function columnCount(cu_oktas) {
  return cu_oktas > 6 ? 3 : cu_oktas > 3 ? 2 : 1;
}

/**
 * X offsets for multi-column layout (bundle line 1482-1488, ee = (width/36)*0.33).
 */
function xOffsets(rt, columnSpacing) {
  if (rt === 3) return [-columnSpacing, 0, columnSpacing];
  if (rt === 2) return [-columnSpacing / 1.33, columnSpacing / 1.33];
  return [0];
}

/**
 * Number of stacked icons (bundle: Ot = floor(It / Ht), where It = condensation-y − boundary-y, Ht = 10).
 */
function stackCount(zsfclcl, pblh, yScale) {
  const It = Math.abs(yScale(zsfclcl) - yScale(pblh));
  return Math.max(1, Math.floor(It / 10));
}

// ─── Icon appenders ───────────────────────────────────────────────────────────

/**
 * drawCumulus — append cumulus cloud SVG icons to the chart.
 * Placement: x from column index, y along the condensation level.
 */
export function drawCumulus(sel, columns, data, yScale, width, gt, margin) {
  if (!columns || columns.length === 0) return;

  const cloudSel = sel.append('svg').attr('class', 'cloudSVG');

  const columnSpacing = (width / data.length) * 0.33; // scale to actual step count (bundle hardcodes 36 = its 3-day canvas)

  columns.forEach(({ index, cu_oktas, accrain }) => {
    const d = data[index];
    if (!d) return;

    const rt = columnCount(cu_oktas);
    const ht = iconSize(cu_oktas, rt);
    const offsets = xOffsets(rt, columnSpacing);
    const stack = stackCount(d.zsfclcl, d.pblh, yScale);

    const cellW = width / data.length;
    const xBase = index * cellW + cellW / 2;
    const yBase = yScale(d.zsfclcl);

    offsets.forEach((xOff) => {
      for (let s = 0; s < stack; s++) {
        const szFactor = accrain > 0 || cu_oktas > 7 ? 2 : cu_oktas <= 2 ? 0.8 : 0.8 + (cu_oktas - 2) * (1.2 / 5);
        const sz = s === stack - 1 ? ht * szFactor : ht * szFactor * (0.6 + (0.4 * s) / Math.max(stack - 1, 1));

        cloudSel
          .append('svg')
          .attr('viewBox', '0 0 72 72')
          .attr('class', 'cloud_svg')
          .attr('x', (xBase + xOff + margin.left - sz / 2) * gt)
          .attr('y', yBase - sz / 2 - (stack - s) * 10 + 5)
          .attr('width', sz)
          .attr('height', sz)
          .append('path')
          .attr('d', CUMULUS_PATH)
          .attr('fill', cloudFillForOktas(cu_oktas))
          .attr('stroke', '#0f172a')
          .attr('stroke-width', 1.5)
          .attr('vector-effect', 'non-scaling-stroke');
      }
    });
  });
}

/**
 * drawRain — append rain drop icons below the condensation level, down to
 * near the bottom of the chart (bundle lines 1543-1563).
 */
export function drawRain(sel, columns, data, yScale, width, gt, margin, chartHeight) {
  if (!columns || columns.length === 0) return;

  const STEP = 15; // bundle: Fe = 15
  const columnSpacing = (width / data.length) * 0.33; // scale to actual step count (bundle hardcodes 36 = its 3-day canvas)

  columns
    .filter(({ accrain }) => accrain > 0)
    .forEach(({ index, cu_oktas, accrain }) => {
      const d = data[index];
      if (!d) return;

      const rt = columnCount(cu_oktas);
      const ht = iconSize(cu_oktas, rt);
      const offsets = xOffsets(rt, columnSpacing);
      const cellW = width / data.length;
      const xBase = index * cellW + cellW / 2;
      const yStart = yScale(d.zsfclcl) + ht / 2 - 57; // bundle: je = zt[xt].y + ht/2 - 57
      const yEnd = chartHeight + margin.top - 22;       // bundle: ma = this.height + this.margin.top - 22
      const dropCount = Math.max(0, Math.floor((yEnd - yStart) / STEP));

      offsets.forEach((xOff) => {
        for (let r = 0; r < dropCount; r++) {
          sel
            .append('svg')
            .attr('viewBox', '0 0 72 72')
            .attr('class', 'c_rain')
            .attr('x', (xBase + xOff + margin.left - ht / 2) * gt)
            .attr('y', yStart + r * STEP)
            .attr('width', ht)
            .attr('height', ht)
            .append('path')
            .attr('d', RAIN_PATH)
            .attr('fill', '#4488ff')
            .attr('stroke', 'none');
        }
      });
    });
}

/**
 * drawLightning — append lightning bolt icons for high-CAPE columns
 * (bundle line 1529: cape > 1000 && cu_oktas > 2 && pblh-zsfclcl > 300).
 */
export function drawLightning(sel, columns, data, yScale, width, gt, margin) {
  if (!columns || columns.length === 0) return;

  const columnSpacing = (width / data.length) * 0.33; // scale to actual step count (bundle hardcodes 36 = its 3-day canvas)

  columns
    .filter(({ cape, cu_oktas, index }) => {
      const d = data[index];
      return cape > 1000 && cu_oktas > 2 && d && (d.pblh - d.zsfclcl) > 300;
    })
    .forEach(({ index, cu_oktas }) => {
      const d = data[index];
      if (!d) return;

      const rt = columnCount(cu_oktas);
      const ht = iconSize(cu_oktas, rt);
      const offsets = xOffsets(rt, columnSpacing);
      const cellW = width / data.length;
      const xBase = index * cellW + cellW / 2;
      const yBase = yScale(d.zsfclcl);

      offsets.forEach((xOff) => {
        sel
          .append('svg')
          .attr('viewBox', '0 0 72 72')
          .attr('class', 'c_lightning')
          .attr('x', (xBase + xOff + margin.left - ht / 2) * gt)
          .attr('y', yBase - ht / 2 - 10 + 6)
          .attr('width', ht)
          .attr('height', ht)
          .append('path')
          .attr('d', LIGHTNING_PATH)
          .attr('fill', '#FFD700')
          .attr('stroke', '#FF8800')
          .attr('stroke-width', 1);
      });
    });
}
